import React, { useEffect, useMemo, useRef, useState } from 'react';
import useApiControl from '../../../common/useApiControl';
import { useSSETarget } from '../../../common/useSseControl';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ReviveRoomPointDialog from '../../dialogs/ReviveRoomPointDialog';
import ConfirmActionDialog from '../../dialogs/ConfirmActionDialog';
import BattleBalanceBar from '../BattleBalanceBar';
import BetSelectionUsersDialog from '../BetSelectionUsersDialog';
import BetChoiceCardPC from '../BetChoiceCardPC';
import { BET_ALREADY_MESSAGE, buildBetDisplayItems, normalizeBetChoices } from '../betLabels';
import { calculateCancelPreview } from '../betCancelPreview';

const parseAnswers = (rawAnswers) => {
  if (Array.isArray(rawAnswers)) {
    return normalizeBetChoices(rawAnswers);
  }

  if (typeof rawAnswers === 'string') {
    const trimmed = rawAnswers.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? normalizeBetChoices(parsed) : [];
    } catch (error) {
      console.error('Error parsing answers JSON', error);
      console.log(rawAnswers);
      return [];
    }
  }

  return [];
};

const normalizeBetPointsPayload = (payload, choices) => {
  const nextPoints = choices.map(() => 0);

  if (!Array.isArray(payload)) {
    return nextPoints;
  }

  payload.forEach((item, fallbackIndex) => {
    if (item && typeof item === 'object') {
      const selectionIndex = Number(item.selection);
      if (!Number.isInteger(selectionIndex) || selectionIndex < 0 || selectionIndex >= nextPoints.length) {
        return;
      }

      nextPoints[selectionIndex] = Number(item.total_point ?? item.totalPoint ?? item.point ?? 0);
      return;
    }

    if (fallbackIndex < nextPoints.length) {
      nextPoints[fallbackIndex] = Number(item || 0);
    }
  });

  return nextPoints;
};

const normalizeBetDetailsPayload = (payload, choices) => {
  const nextDetails = choices.map(() => null);

  if (!Array.isArray(payload)) {
    return nextDetails;
  }

  payload.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const selectionIndex = Number(item.selection);
    if (!Number.isInteger(selectionIndex) || selectionIndex < 0 || selectionIndex >= nextDetails.length) {
      return;
    }

    const detail = item.latest_bet || item;
    const point = Number(detail.point ?? item.total_point ?? item.totalPoint ?? 0);
    const rate = detail.rate ?? detail.option_rate ?? item.rate ?? item.option_rate ?? null;
    const expectedPayout = detail.expected_payout ?? item.expected_payout ?? (
      rate !== null && Number.isFinite(Number(rate)) ? Math.floor(Number(rate) * point) : null
    );

    nextDetails[selectionIndex] = {
      ...detail,
      selection: selectionIndex,
      point,
      rate,
      option_rate: detail.option_rate ?? rate,
      expected_payout: expectedPayout,
    };
  });

  return nextDetails;
};

const formatRate = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const rateValue = typeof value === 'object' ? (value.rate ?? value.optional_rate ?? value.base_rate) : value;
  if (rateValue === null || rateValue === undefined || rateValue === '') {
    return '-';
  }

  const numericValue = Number(rateValue);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : String(rateValue);
};

const BET_ENTRY_STATUS = {
  NOT_BET: 'notBet',
  EDITING: 'editing',
  PLACED: 'placed',
};

const RoundDitail = ({
  event,
  round,
  user,
  updateUser,
  displayPoint,
  highlightedBetSelections = [],
  betPopupUsersBySelection = {},
  setDisplayPoint,
  setPointChangeAmount,
  roundRateData: providedRoundRateData,
  summaryData,
  timePhase,
  isRoomPointInsufficient,
}) => {
  const [viewPoints, setViewPoints] = useState([]);
  const [viewBetDetails, setViewBetDetails] = useState([]);
  const [viewRate, setViewRate] = useState([]);
  const roundStatus =
    event?.bet_close_mode === 'manual' && timePhase === 'ended'
      ? 'active'
      : (timePhase || 'waiting');
  const [highlightedIndexes, setHighlightedIndexes] = useState([]);
  const [choices, setChoices] = useState(() => parseAnswers(round.answers));
  const [pendingBetPoints, setPendingBetPoints] = useState(() => parseAnswers(round.answers).map(() => 0));
  const [isBetSubmitting, setIsBetSubmitting] = useState(false);
  const [isBetCancelling, setIsBetCancelling] = useState(false);
  const [cancelConfirmDialogVisible, setCancelConfirmDialogVisible] = useState(false);
  const [reviveDialogVisible, setReviveDialogVisible] = useState(false);
  const [reviveResult, setReviveResult] = useState(null);
  const [reviveErrorMessage, setReviveErrorMessage] = useState('');
  const [betUsersDialogTarget, setBetUsersDialogTarget] = useState(null);
  const roundRateMessage = useSSETarget('roundRate');
  const ignoredRoundRateSeqRef = useRef(roundRateMessage?.__seq ?? null);
  const roundRateData = providedRoundRateData ?? (
    roundRateMessage?.__seq && roundRateMessage.__seq !== ignoredRoundRateSeqRef.current
      ? roundRateMessage.payload
      : null
  );

  const roundNumberRef = useRef(round.number);

  const normalizeRatePayload = (payload, currentRates) => {
    const nextRates = choices.map((_, index) => currentRates[index] ?? '-');
    const assignRate = (index, value) => {
      if (index < 0 || index >= nextRates.length || value === null || value === undefined || value === '') {
        return;
      }

      nextRates[index] = formatRate(value);
    };

    if (Array.isArray(payload)) {
      payload.forEach((value, index) => assignRate(index, value));
      return nextRates;
    }

    if (payload && typeof payload === 'object') {
      Object.entries(payload).forEach(([key, value]) => assignRate(Number(key), value));
      return nextRates;
    }

    return null;
  };

  useEffect(() => {
    roundNumberRef.current = round.number;
  }, [round.number]);

  const values = useMemo(() => {
    const point = user.point || 0;
    const min = event.minimum_bet || 0;
    const max = event.max_bet || Infinity;

    if (point < min) {
      return [];
    }

    const lenPoint = point.toString().length;
    let base1 = 10 ** (lenPoint - 1);
    let base2 = 10 ** (lenPoint - 2);

    if (base1 > max / 10) {
      const maxDigits = Math.floor(Math.log10(max));
      base1 = 10 ** (maxDigits - 1);
      base2 = 10 ** (maxDigits - 2);
    }

    const betValues = [base1, base2].filter((value) => value >= min && value <= max);
    // 暫定対応: ベット額候補生成ロジックを見直すまで、候補0件の場合は最小ベット額を表示する。
    return betValues.length > 0 ? betValues : [min];
  }, [event.max_bet, event.minimum_bet, user.point]);

  const params1 = useMemo(
    () => ({
      event_id: event?.id,
      number: event?.round,
    }),
    [event],
  );

  const { data: data1, executeRequest: executeRequest1 } = useApiControl({
    action: '/get_my_bet',
    params: params1,
    method: 'POST',
  });

  const { executeRequest: executeRequest2 } = useApiControl({
    action: '/bet_action',
    method: 'POST',
  });

  const { executeRequest: executeCancelBet } = useApiControl({
    action: '/cancel_bet_action',
    method: 'POST',
  });

  const { loading: revivingPoint, executeRequest: executeRevivePoint } = useApiControl({
    action: '/revive_event_point',
    method: 'POST',
  });

  useEffect(() => {
    if (data1?.bets) {
      setViewPoints(normalizeBetPointsPayload(data1.bets, choices));
      setViewBetDetails(normalizeBetDetailsPayload(data1.bets, choices));
    }
  }, [choices, data1]);

  useEffect(() => {
    executeRequest1();
  }, []);

  useEffect(() => {
    ignoredRoundRateSeqRef.current = roundRateMessage?.__seq ?? null;
    setPendingBetPoints(choices.map(() => 0));
    setViewPoints(choices.map(() => 0));
    setViewBetDetails(choices.map(() => null));
    setViewRate(choices.map(() => '-'));
  }, [round.number]);

  useEffect(() => {
    const parsedChoices = parseAnswers(round.answers);
    if (round.answers) {
      console.log(round.answers);
    }
    setChoices(parsedChoices);
  }, [round.answers]);

  useEffect(() => {
    setPendingBetPoints(choices.map(() => 0));
    setViewPoints(choices.map(() => 0));
    setViewBetDetails(choices.map(() => null));
    setViewRate(choices.map(() => '-'));
  }, [choices]);

  useEffect(() => {
    if (!roundRateData) {
      return;
    }

    setViewRate((currentRates) => {
      const formatted = normalizeRatePayload(roundRateData, currentRates);
      if (formatted) {
        return formatted;
      }
      return currentRates;
    });
  }, [roundRateData, choices]);

  const displayRates = useMemo(() => {
    if (!roundRateData) {
      return viewRate;
    }

    return normalizeRatePayload(roundRateData, viewRate) || viewRate;
  }, [roundRateData, choices, viewRate]);

  const betDisplayItems = useMemo(
    () => buildBetDisplayItems(choices),
    [choices],
  );

  const confirmedBetIndex = useMemo(
    () => viewPoints.findIndex((point) => Number(point || 0) > 0),
    [viewPoints],
  );
  const hasConfirmedBet = confirmedBetIndex >= 0;
  const confirmedBetPoint = confirmedBetIndex >= 0 ? Number(viewPoints[confirmedBetIndex] || 0) : 0;
  const cancelPreview = useMemo(
    () => calculateCancelPreview(confirmedBetPoint),
    [confirmedBetPoint],
  );
  const cancelConfirmMessage = `ベットをキャンセルしますか？\n\nキャンセル時 ${cancelPreview.refundPoint}pt返却\n手数料 ${cancelPreview.feePoint}pt`;
  const pendingBetIndex = useMemo(
    () => pendingBetPoints.findIndex((point) => Number(point || 0) > 0),
    [pendingBetPoints],
  );
  const betEntryStatus = hasConfirmedBet
    ? BET_ENTRY_STATUS.PLACED
    : pendingBetIndex >= 0 ? BET_ENTRY_STATUS.EDITING : BET_ENTRY_STATUS.NOT_BET;

  const canBetOnSelection = (index) => !hasConfirmedBet && (pendingBetIndex < 0 || pendingBetIndex === index);

  const addPendingBet = (index, value) => {
    if (!canBetOnSelection(index)) {
      return;
    }

    const betValue = parseInt(value, 10);
    if (!Number.isFinite(betValue) || betValue <= 0) {
      return;
    }

    setPendingBetPoints((currentPendingPoints) => {
      const currentPendingTotal = currentPendingPoints.reduce((sum, point) => sum + Number(point || 0), 0);
      const currentSelectionPending = Number(currentPendingPoints[index] || 0);

      if (betValue > Math.max(displayPoint - currentPendingTotal, 0)) {
        return currentPendingPoints;
      }

      if (event.max_bet < betValue + currentSelectionPending) {
        return currentPendingPoints;
      }

      const updatedPendingPoints = [...currentPendingPoints];
      updatedPendingPoints[index] = currentSelectionPending + betValue;
      return updatedPendingPoints;
    });


    setHighlightedIndexes((prev) => [...prev, index]);
    setTimeout(() => {
      setHighlightedIndexes((prev) => prev.filter((item) => item !== index));
    }, 500);
  };

  const confirmBet = (index) => {
    const pendingPoint = Number(pendingBetPoints[index] || 0);
    if (pendingPoint <= 0 || isBetSubmitting || !(roundStatus === 'fast' || roundStatus === 'active')) {
      return;
    }

    setIsBetSubmitting(true);
    executeRequest2({
      e: event.id,
      r: roundNumberRef.current,
      a: index,
      p: pendingPoint,
      t: new Date().toISOString(),
    })
      .then((response) => {
        const savedBet = response?.bet ?? {
          selection: index,
          point: pendingPoint,
          rate: displayRates[index],
          option_rate: displayRates[index],
          expected_payout: Number.isFinite(Number(displayRates[index]))
            ? Math.floor(Number(displayRates[index]) * pendingPoint)
            : null,
        };

        setPendingBetPoints((currentPendingPoints) => {
          const updatedPendingPoints = [...currentPendingPoints];
          updatedPendingPoints[index] = 0;
          return updatedPendingPoints;
        });
        setDisplayPoint((currentDisplayPoint) => currentDisplayPoint - pendingPoint);
        setViewPoints((currentViewPoints) => {
          const updatedViewPoints = [...currentViewPoints];
          updatedViewPoints[index] = Number(updatedViewPoints[index] || 0) + pendingPoint;
          return updatedViewPoints;
        });
        setViewBetDetails((currentDetails) => {
          const updatedDetails = [...currentDetails];
          updatedDetails[index] = savedBet;
          return updatedDetails;
        });
        setIsBetSubmitting(false);
      })
      .catch((error) => {
        console.error(error);
        if (error?.response?.status === 409) {
          alert(error.response?.data?.message || BET_ALREADY_MESSAGE);
          executeRequest1().catch((fetchError) => {
            console.error(fetchError);
          });
        }
        setIsBetSubmitting(false);
        updateUser();
      });
  };

  const cancelBet = (index) => {
    setPendingBetPoints((currentPendingPoints) => {
      const updatedPendingPoints = [...currentPendingPoints];
      updatedPendingPoints[index] = 0;
      return updatedPendingPoints;
    });
  };

  const openCancelConfirmDialog = () => {
    if (!hasConfirmedBet || isBetCancelling || !(roundStatus === 'fast' || roundStatus === 'active')) {
      return;
    }

    setCancelConfirmDialogVisible(true);
  };

  const cancelConfirmedBet = () => {
    if (!hasConfirmedBet || isBetCancelling || !(roundStatus === 'fast' || roundStatus === 'active')) {
      return;
    }

    setIsBetCancelling(true);
    executeCancelBet({
      e: event.id,
      r: roundNumberRef.current,
      t: new Date().toISOString(),
    })
      .then((response) => {
        const refundPoint = Number(response?.refund_point || 0);
        setPendingBetPoints(choices.map(() => 0));
        setViewPoints(choices.map(() => 0));
        setViewBetDetails(choices.map(() => null));
        setDisplayPoint((currentDisplayPoint) => (
          response?.room_point !== null && response?.room_point !== undefined
            ? Number(response.room_point)
            : currentDisplayPoint + refundPoint
        ));
        if (setPointChangeAmount && response?.point_change) {
          setPointChangeAmount(response.point_change);
        }
      })
      .catch((error) => {
        console.error(error);
        alert(error?.response?.data?.message || 'ベットをキャンセルできませんでした。');
      })
      .finally(() => {
        setIsBetCancelling(false);
        setCancelConfirmDialogVisible(false);
        updateUser();
      });
  };

  const reviveRequiredPoint = Math.max((event?.minimum_bet || 0) - (user?.point || 0), 0);
  const roomPointInsufficient = isRoomPointInsufficient ?? reviveRequiredPoint > 0;
  const reviveCount = Number(user?.revive_count || 0);
  const remainingRevives = Math.max(3 - reviveCount, 0);
  const alreadyRevivedThisRound =
    Number(user?.last_revived_round || 0) === Number(event?.round || 0);
  const reviveUnavailableMessage =
    reviveRequiredPoint <= 0
      ? ''
      : event?.completion_status && event.completion_status !== 'active'
        ? '終了したルームでは復活できません。'
        : (user?.ticket || 0) < reviveRequiredPoint
          ? '参加チケットが不足しているため復活できません。'
          : remainingRevives <= 0
              ? 'このイベントでの復活回数上限に達しています。'
              : alreadyRevivedThisRound
                ? 'このラウンドではすでに復活済みです。'
                : !(roundStatus === 'fast' || roundStatus === 'active')
                  ? 'ベット可能時間中のみ復活できます。'
                  : '';
  const canReviveByRule = reviveRequiredPoint > 0 && !reviveUnavailableMessage;
  const showReviveNotice =
    round.type === 0 && roomPointInsufficient && reviveRequiredPoint > 0;

  const revivePreview = {
    moved_points: reviveRequiredPoint,
    before_ticket: Number(user?.ticket || 0),
    ticket: Math.max(Number(user?.ticket || 0) - reviveRequiredPoint, 0),
    before_room_point: Number(user?.point || 0),
    room_point: Number(user?.point || 0) + reviveRequiredPoint,
    revive_count: reviveCount,
    remaining_revives: remainingRevives,
  };

  const openReviveDialog = () => {
    setReviveResult(null);
    setReviveErrorMessage(reviveUnavailableMessage);
    setReviveDialogVisible(true);
  };

  const handleReviveConfirm = () => {
    executeRevivePoint({ event_id: event.id })
      .then((response) => {
        setReviveResult(response);
        setReviveErrorMessage('');
        updateUser();
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          '復活ポイントの移送に失敗しました。';
        setReviveErrorMessage(message);
      });
  };

  const infoMsgSx = {
    mt: 0.75,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    fontSize: '0.95em',
    lineHeight: 1.4,
    bgcolor: 'rgba(0,0,0,0.04)',
    border: '1px solid rgba(0,0,0,0.10)',
    color: 'text.secondary',
  };

  const warnMsgSx = {
    ...infoMsgSx,
    bgcolor: 'rgba(255, 193, 7, 0.12)',
    border: '1px solid rgba(255, 193, 7, 0.35)',
    color: 'rgba(120, 84, 0, 0.95)',
    fontWeight: 700,
  };

  const reviveNoticeSx = {
    mt: 1,
    p: 1.5,
    borderRadius: '12px',
    background:
      'linear-gradient(135deg, rgba(255, 183, 77, 0.18) 0%, rgba(255, 224, 178, 0.36) 100%)',
    border: '1px solid rgba(245, 124, 0, 0.28)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
  };

  const betChoiceColumnCount = Math.min(Math.max(betDisplayItems.length, 2), 4);
  const betCardGridSx = {
    display: 'grid',
    gridTemplateColumns: `repeat(${betChoiceColumnCount}, minmax(0, 1fr))`,
    gap: betChoiceColumnCount >= 4 ? 1 : 1.5,
    alignItems: 'stretch',
  };
  const selectedBetUsers = betUsersDialogTarget
    ? (betPopupUsersBySelection[betUsersDialogTarget.index] || betPopupUsersBySelection[String(betUsersDialogTarget.index)] || [])
    : [];

  return (
    <>
      <div
        style={{
          padding: '8px 16px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          fontSize: '14px',
          color: '#444',
          boxSizing: 'border-box',
          width: '100%',
        }}
      >

        {roomPointInsufficient && (
          <Box sx={warnMsgSx}>ルームチップ不足です。</Box>
        )}

        {(choices.length === 0 || roundStatus === 'waiting') && (
          <Box sx={{ ...infoMsgSx, bgcolor: 'rgba(0,0,0,0.03)' }}>
            ゲーム開始までお待ちください
          </Box>
        )}

        {roundStatus === 'ended' && (
          <Box sx={{ ...infoMsgSx, bgcolor: 'rgba(0,0,0,0.03)' }}>
            ラウンドは終了しました
          </Box>
        )}

        {round.type === 0 && choices.length >= 2 && (
          <BattleBalanceBar
            roundKey={round.id ?? round.number}
            choices={choices}
            displayItems={betDisplayItems}
            highlightedSelections={highlightedBetSelections}
            betUsersBySelection={betPopupUsersBySelection}
            onOpenBetUsers={setBetUsersDialogTarget}
            summaryData={summaryData}
            rateData={displayRates}
          />
        )}

        {showReviveNotice && (
          <Box sx={reviveNoticeSx}>
            <Box sx={{ fontSize: '13px', fontWeight: 800, color: '#9a3412', mb: 0.75 }}>
              ルームチップ不足でベットできません
            </Box>
            <Box sx={{ fontSize: '12px', color: 'rgba(120, 84, 0, 0.92)', lineHeight: 1.6, mb: 1.25 }}>
              参加チケットから {reviveRequiredPoint} 枚を移して、最小ベット可能状態に戻せます。
              <br />
              1ラウンド1回 / 1イベント最大3回 / 不足分のみ移送されます。
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ fontSize: '12px', color: 'rgba(120, 84, 0, 0.88)', lineHeight: 1.6 }}>
                参加チケット {user?.ticket || 0}枚 → {revivePreview.ticket}枚
                <br />
                Room {user?.point || 0} pt → {revivePreview.room_point} pt
              </Box>
              <Button
                variant="contained"
                onClick={openReviveDialog}
                disabled={!canReviveByRule}
                sx={{
                  minWidth: '160px',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: '999px',
                  boxShadow: 'none',
                  background: 'linear-gradient(135deg, #ef6c00 0%, #fb8c00 100%)',
                  '&:hover': {
                    boxShadow: '0 6px 14px rgba(239, 108, 0, 0.24)',
                    background: 'linear-gradient(135deg, #e65100 0%, #f57c00 100%)',
                  },
                }}
              >
                復活ポイントを確認
              </Button>
            </Box>
            {reviveUnavailableMessage && (
              <Box sx={{ mt: 1, fontSize: '12px', color: '#9a3412', fontWeight: 700 }}>
                {reviveUnavailableMessage}
              </Box>
            )}
          </Box>
        )}

        {!roomPointInsufficient && round.type === 0 && (
          <Box sx={betCardGridSx}>
            {betDisplayItems.map((item) => (
              <BetChoiceCardPC
                key={item.index}
                choice={item.choice}
                displayLabel={item.label}
                colorSet={item.color}
                index={item.index}
                rate={displayRates[item.index] ?? '-'}
                betPoint={viewPoints[item.index]}
                betDetail={viewBetDetails[item.index]}
                pendingPoint={pendingBetPoints[item.index]}
                betValues={values}
                displayPoint={displayPoint}
                maxBet={event.max_bet}
                canBetTime={roundStatus === 'fast' || roundStatus === 'active'}
                isHighlighted={highlightedIndexes.includes(item.index) || highlightedBetSelections.includes(item.index)}
                disabledBySideLock={!canBetOnSelection(item.index)}
                sideLockMessage={BET_ALREADY_MESSAGE}
                betEntryStatus={betEntryStatus}
                onBet={addPendingBet}
                onConfirmBet={confirmBet}
                onCancelBet={cancelBet}
                onCancelConfirmedBet={openCancelConfirmDialog}
                isBetSubmitting={isBetSubmitting}
                isBetCancelling={isBetCancelling}
                canCancelConfirmedBet={hasConfirmedBet && confirmedBetIndex === item.index && (roundStatus === 'fast' || roundStatus === 'active')}
                compact={false}
                choiceColumnCount={betChoiceColumnCount}
              />
            ))}
          </Box>
        )}


      </div>

      <ReviveRoomPointDialog
        isVisible={reviveDialogVisible}
        onClose={() => setReviveDialogVisible(false)}
        onConfirm={handleReviveConfirm}
        loading={revivingPoint}
        canRevive={canReviveByRule}
        preview={revivePreview}
        result={reviveResult}
        errorMessage={reviveErrorMessage}
      />
      <ConfirmActionDialog
        isVisible={cancelConfirmDialogVisible}
        onClose={() => setCancelConfirmDialogVisible(false)}
        onConfirm={cancelConfirmedBet}
        message={cancelConfirmMessage}
        confirmLabel="キャンセルする"
        cancelLabel="戻る"
        loading={isBetCancelling}
      />
      <BetSelectionUsersDialog
        colorSet={betUsersDialogTarget?.color}
        displayLabel={betUsersDialogTarget?.label || betUsersDialogTarget?.shortLabel || '選択肢'}
        isVisible={Boolean(betUsersDialogTarget)}
        onClose={() => setBetUsersDialogTarget(null)}
        responsiveMode="desktop"
        users={selectedBetUsers}
      />
    </>
  );
};

export default RoundDitail;
